<script lang="ts" setup>
import type { ResponsiveImageType } from "vue-datocms";
import type { CustomLinkRecord, HomepageHeroSectionRecord, PageLinkRecord } from "~~/server/graphql/datocms/generated";
import { Image as DatocmsImage } from "vue-datocms";

const props = defineProps<{ data: HomepageHeroSectionRecord }>();

const localePath = useLocalePath();

const heroImageLink = computed(() => {
  if (props.data.pageLink.at(0)?._modelApiKey === "custom_link") {
    const customLink = props.data.pageLink.at(0) as CustomLinkRecord;

    return {
      id: customLink.id,
      label: customLink.text,
      to: customLink.customLink,
    };
  }

  if (props.data.pageLink.at(0)?._modelApiKey === "page_link") {
    const pageLink = props.data.pageLink.at(0) as PageLinkRecord;

    return {
      id: pageLink.id,
      label: pageLink.text,
      to: getUrl(pageLink.pageLink!.slug, localePath),
    };
  }

  return null;
});
</script>

<template>
  <div class="mb-3xl lg:mb-5xl relative">
    <Vector class="vector--background-light absolute -left-31.25 top-6.25 z-20 hidden h-30 rotate-15 xl:block" />
    <Vector class="vector--background-light absolute -left-37.5 top-50 z-20 hidden h-20 rotate-45 xl:block" />
    <Vector class="vector--background-light absolute left-87.5 top-12.5 z-20 hidden h-80 rotate-0 xl:block" />

    <div class="mb-xs gap-xs relative z-30 flex flex-wrap lg:flex-nowrap">
      <div class="order-2 flex items-center lg:order-1">
        <div class="flex flex-col items-start p-md lg:p-xl">
          <h2 class="mb-md lg:mb-lg">
            {{ data.heroTitle }}
          </h2>

          <div class="mb-md lg:mb-lg" v-html="data.heroText" />

          <UButton
            v-if="heroImageLink"
            variant="outline" color="primary" size="xl" :ui="{ base: 'mb-lg lg:mb-0 leading-7' }"
            :to="getUrl(heroImageLink.to, localePath)" :target="getTarget(heroImageLink.to)"
          >
            {{ heroImageLink.label }}
          </UButton>
        </div>
      </div>

      <div class="relative order-1 lg:order-2">
        <NuxtLink v-if="heroImageLink" :to="getUrl(heroImageLink.to, localePath)" :target="getTarget(heroImageLink.to)" class="group">
          <div class="overflow-hidden rounded-lg">
            <DatocmsImage
              v-if="data.heroImage" :data="data.heroImage.responsiveImage as ResponsiveImageType"
              :lazy-load="false" object-fit="cover" class="transition-transform duration-300 ease-in-out group-hover:scale-105"
            />
          </div>
        </NuxtLink>

        <DatocmsImage
          v-if="!heroImageLink && data.heroImage" :data="data.heroImage.responsiveImage as ResponsiveImageType"
          :lazy-load="false" object-fit="cover" class="rounded-lg"
        />

        <VectorWithStroke class="vector--main-image absolute -left-3 -top-3 h-[1.95rem] w-8 rotate-80 xl:h-18 xl:w-18.5" />
      </div>
    </div>

    <div class="gap-xs relative z-30 grid grid-cols-1 lg:grid-cols-2">
      <div v-for="(imageLink, index) in data.imageLinks" :key="imageLink.id">
        <HomepageHeroBanner
          :banner="imageLink"
          :index="index"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.vector--main-image {
  --esc-vector-fill-color: #c81414;
  --esc-vector-opacity: 1;
  --esc-vector-mix-blend-mode: normal;
}

.vector--background-light {
  --esc-vector-fill-color: #f3f1ec;
  --esc-vector-opacity: 1;
  --esc-vector-mix-blend-mode: normal;
}
</style>
